import { z } from "zod";

/** Fixed partnership categories offered on the "Partner with us" form. */
export const PARTNER_TYPES = [
  "experiences",
  "stays",
  "restaurants",
  "transport",
  "spa_wellness",
  "other",
] as const;
export type PartnerType = (typeof PARTNER_TYPES)[number];

/** How a partner usually confirms availability with our team. */
export const AVAILABILITY_METHODS = ["whatsapp", "phone", "email"] as const;
export type AvailabilityMethod = (typeof AVAILABILITY_METHODS)[number];

const emptyToUndefined = (v: unknown) =>
  typeof v === "string" && v.trim() === "" ? undefined : v;

const numberOrUndefined = (v: unknown) =>
  v === "" || v === null || v === undefined ? undefined : Number(v);

/** Wire + validation contract for a partner lead submission. */
export const partnerLeadSchema = z.object({
  fullName: z.string().trim().min(2).max(120),
  phone: z.string().trim().min(4).max(40),
  email: z.string().trim().email().max(200),
  partnerType: z.enum(PARTNER_TYPES),
  city: z.string().trim().min(1).max(120),
  experienceName: z.preprocess(emptyToUndefined, z.string().trim().max(200).optional()),
  availabilityMethod: z.preprocess(
    emptyToUndefined,
    z.enum(AVAILABILITY_METHODS).optional(),
  ),
  avgPricePerPerson: z.preprocess(
    numberOrUndefined,
    z.number().int().positive().max(1_000_000).optional(),
  ),
  websiteUrl: z.preprocess(emptyToUndefined, z.string().url().max(500).optional()),
  instagramUrl: z.preprocess(emptyToUndefined, z.string().url().max(500).optional()),
  description: z.string().trim().min(1).max(300),
  consent: z.literal(true),
  lang: z.string().min(2).max(5).default("en"),
});

export type PartnerLeadInput = z.infer<typeof partnerLeadSchema>;

/** Follow-up pipeline for inbound partner leads (admin-managed). */
export const PARTNER_LEAD_STATUSES = ["new", "contacted", "won", "lost"] as const;
export type PartnerLeadStatus = (typeof PARTNER_LEAD_STATUSES)[number];

/** Wire shape of a stored partner lead as consumed by the admin. */
export interface PartnerLeadDTO {
  id: string;
  fullName: string;
  phone: string;
  email: string;
  partnerType: string;
  city: string;
  experienceName: string | null;
  availabilityMethod: string | null;
  avgPricePerPerson: number | null;
  websiteUrl: string | null;
  instagramUrl: string | null;
  description: string;
  consent: boolean;
  lang: string;
  status: string;
  /** First time staff opened the lead; null = unseen. */
  seenAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Query contract for the admin partner-leads list. */
export const partnerLeadsListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(20),
  status: z.preprocess(emptyToUndefined, z.enum(PARTNER_LEAD_STATUSES).optional()),
  q: z.preprocess(emptyToUndefined, z.string().trim().max(200).optional()),
});
export type PartnerLeadsListQuery = z.infer<typeof partnerLeadsListQuerySchema>;
